/**
 * AdminDashboardPage — permission-aware Events management.
 *
 * Content is edited via the form dialog (PUT). Lifecycle state changes happen
 * ONLY through the dedicated approve/publish/archive endpoints — never the form.
 * The backend enforces every rule; the UI merely reflects the RBAC matrix.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { CalendarDays, Plus, CheckCircle2, Send, Clock, Archive, Trash2, Pencil } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/app/providers/AuthProvider';
import { listEvents, createEvent, updateEvent, approveEvent, publishEvent, archiveEvent, deleteEvent } from '@/services/api/events';
import { listPlaces } from '@/services/api/places';
import { listActivities } from '@/services/api/activities';
import { formatApiError } from '@/services/api/client';
import { CoverImage } from '@/components/media/Cover';
import { EventFormDialog } from '@/pages/admin/forms/EventFormDialog';
import { DASH } from '@/constants/testIds';
import { fmtRome } from '@/lib/romeTime';

const STATUS_STYLES = {
        approved: 'bg-emerald-400/10 text-emerald-300',
        pending: 'bg-amber-400/10 text-amber-300',
        rejected: 'bg-rose-400/10 text-rose-300',
        published: 'bg-sky-400/10 text-sky-300',
        unpublished: 'bg-slate-700/40 text-slate-400',
};

// UI-only Italian labels — non modificano enum/DB/API.
const STATUS_LABELS = {
        approved: 'Approvato',
        pending: 'In attesa',
        rejected: 'Rifiutato',
        published: 'Pubblicato',
        unpublished: 'Non pubblicato',
        draft: 'Bozza',
        archived: 'Archiviato',
};
const ROLE_LABELS = {
        super_admin: 'Super Admin',
        municipality_admin: 'Amministratore Comune',
        municipality_operator: 'Operatore Comune',
        local_business: 'Attività Locale',
        visitor: 'Visitatore',
};
const roleLabel = (r) => ROLE_LABELS[r] || r;
const statusLabel = (s) => STATUS_LABELS[s] || s;

export default function AdminDashboardPage() {
        const { user, can } = useAuth();
        const qc = useQueryClient();
        const [dialog, setDialog] = useState({ open: false, initial: null });

        const events = useQuery({ queryKey: ['events'], queryFn: listEvents });
        const places = useQuery({ queryKey: ['places'], queryFn: listPlaces, retry: false });
        const activities = useQuery({ queryKey: ['activities'], queryFn: listActivities, retry: false });

        const invalidate = () => qc.invalidateQueries({ queryKey: ['events'] });
        const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

        const createMut = useMutation({
                mutationFn: createEvent,
                onSuccess: () => { toast.success('Evento creato'); setDialog({ open: false, initial: null }); invalidate(); },
                onError: err,
        });
        const updateMut = useMutation({
                mutationFn: ({ id, body }) => updateEvent(id, body),
                onSuccess: () => { toast.success('Evento aggiornato'); setDialog({ open: false, initial: null }); invalidate(); },
                onError: err,
        });
        const approveMut = useMutation({ mutationFn: approveEvent, onSuccess: () => { toast.success('Evento approvato'); invalidate(); }, onError: err });
        const publishMut = useMutation({ mutationFn: publishEvent, onSuccess: () => { toast.success('Evento pubblicato'); invalidate(); }, onError: err });
        const archiveMut = useMutation({ mutationFn: archiveEvent, onSuccess: () => { toast.success('Evento archiviato'); invalidate(); }, onError: err });
        const deleteMut = useMutation({ mutationFn: deleteEvent, onSuccess: () => { toast.success('Evento eliminato'); invalidate(); }, onError: err });

        const onSubmit = (body) => {
                if (dialog.initial) updateMut.mutate({ id: dialog.initial.id, body });
                else createMut.mutate(body);
        };

        const list = events.data ?? [];

        return (
                <motion.div data-testid={DASH.page} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mx-auto max-w-5xl">
                        <div className="flex flex-wrap items-end justify-between gap-4">
                                <div>
                                        <h1 data-testid={DASH.welcome} className="text-3xl font-extrabold sm:text-4xl" style={{ fontFamily: "'Poppins', sans-serif" }}>Eventi</h1>
                                        <p className="mt-1 text-slate-400">Ciao {user?.full_name} · ruolo <span className="text-emerald-300">{roleLabel(user?.role)}</span></p>
                                </div>
                                {can('create_content') && (
                                        <Button data-testid={DASH.createButton} onClick={() => setDialog({ open: true, initial: null })} className="bg-emerald-400 text-slate-950 hover:bg-emerald-300">
                                                <Plus size={16} className="mr-1.5" /> Nuovo evento
                                        </Button>
                                )}
                        </div>

                        <EventFormDialog
                                open={dialog.open}
                                onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
                                initial={dialog.initial}
                                places={places.data ?? []}
                                activities={activities.data ?? []}
                                onSubmit={onSubmit}
                                pending={createMut.isPending || updateMut.isPending}
                        />

                        <div className="mt-8 grid gap-4 sm:grid-cols-3">
                                <div data-testid={DASH.statEvents} className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
                                        <div className="flex items-center justify-between"><CalendarDays size={18} className="text-emerald-300" /><span className="text-2xl font-bold">{list.length}</span></div>
                                        <p className="mt-2 text-xs uppercase tracking-widest text-slate-500">Eventi visibili</p>
                                </div>
                                <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
                                        <div className="flex items-center justify-between"><Clock size={18} className="text-amber-300" /><span className="text-2xl font-bold">{list.filter((e) => e.approval_status === 'pending').length}</span></div>
                                        <p className="mt-2 text-xs uppercase tracking-widest text-slate-500">In attesa</p>
                                </div>
                                <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
                                        <div className="flex items-center justify-between"><Send size={18} className="text-sky-300" /><span className="text-2xl font-bold">{list.filter((e) => e.publication_status === 'published').length}</span></div>
                                        <p className="mt-2 text-xs uppercase tracking-widest text-slate-500">Pubblicati</p>
                                </div>
                        </div>

                        <h2 className="mt-10 text-lg font-bold">Eventi</h2>
                        {!can('manage_content') && (
                                <p data-testid={DASH.noPermHint} className="mt-1 text-sm text-slate-500">Come <span className="text-emerald-300">{roleLabel(user?.role)}</span> vedi e gestisci solo i tuoi contenuti.</p>
                        )}
                        <div data-testid={DASH.eventsList} className="mt-4 space-y-2">
                                {events.isLoading && <p className="text-slate-500">Caricamento…</p>}
                                {!events.isLoading && list.length === 0 && <p className="text-slate-500">Nessun evento.</p>}
                                {list.map((ev) => (
                                        <div key={ev.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3">
                                                <div className="flex min-w-0 items-center gap-3">
                                                        <CoverImage url={ev.cover_url} icon={CalendarDays} className="h-12 w-12 shrink-0 rounded-lg" />
                                                        <div className="min-w-0">
                                                                <p className="font-semibold text-slate-100">{ev.title}</p>
                                                                <p className="text-xs text-slate-500">{fmtRome(ev.start_at, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · {ev.slug}</p>
                                                        </div>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                        <Badge className={STATUS_STYLES[ev.approval_status] || ''}>{statusLabel(ev.approval_status)}</Badge>
                                                        <Badge className={STATUS_STYLES[ev.publication_status] || ''}>{statusLabel(ev.publication_status)}</Badge>
                                                        <Button size="sm" variant="outline" data-testid={DASH.editButton} onClick={() => setDialog({ open: true, initial: ev })} className="border-slate-600 text-slate-300 hover:bg-slate-800"><Pencil size={13} /></Button>
                                                        {can('approve_content') && ev.approval_status !== 'approved' && (
                                                                <Button size="sm" variant="outline" data-testid={DASH.approveButton} onClick={() => approveMut.mutate(ev.id)} className="border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"><CheckCircle2 size={14} className="mr-1" /> Approva</Button>
                                                        )}
                                                        {can('publish_content') && ev.approval_status === 'approved' && ev.publication_status !== 'published' && (
                                                                <Button size="sm" variant="outline" data-testid={DASH.publishButton} onClick={() => publishMut.mutate(ev.id)} className="border-sky-400/40 text-sky-300 hover:bg-sky-400/10"><Send size={14} className="mr-1" /> Pubblica</Button>
                                                        )}
                                                        {can('manage_content') && ev.status !== 'archived' && (
                                                                <Button size="sm" variant="outline" data-testid="dashboard-archive-button" onClick={() => archiveMut.mutate(ev.id)} className="border-slate-600 text-slate-300 hover:bg-slate-800"><Archive size={14} /></Button>
                                                        )}
                                                        <Button size="sm" variant="outline" data-testid="dashboard-delete-button" onClick={() => deleteMut.mutate(ev.id)} className="border-rose-400/40 text-rose-300 hover:bg-rose-400/10"><Trash2 size={14} /></Button>
                                                </div>
                                        </div>
                                ))}
                        </div>
                </motion.div>
        );
}
